import { Component, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/user/auth.service';
import { GoogleAuthComponent } from '../auth/google-auth.component';
import { AUTH_MESSAGES } from '../../constants/auth-messages.constants';

@Component({
  selector: 'app-sign-in-dialog',
  standalone: true,
  imports: [CommonModule, GoogleAuthComponent],
  template: `
    <!-- Sign-in Dialog Overlay: uses *ngIf from parent -->
    <div
      class="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center transition-opacity duration-300"
      (click)="closeDialog.emit()"
    >
      <div
        class="bg-white dark:bg-dark-primary p-6 rounded-lg shadow-xl w-full max-w-sm text-center relative"
        (click)="$event.stopPropagation()"
      >
        <h2 class="text-xl font-semibold mb-4 dark:text-white">
          {{ authMessages.signInTitle }}
        </h2>

        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {{ authMessages.signInSubtitle }}
        </p>

        <ul
          class="text-sm text-gray-600 dark:text-gray-400 mb-4 space-y-1 inline-block text-left"
        >
          <li
            *ngFor="let feature of authMessages.features"
            class="flex items-center"
          >
            <svg
              class="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span>{{ feature.text }}</span>
          </li>
        </ul>

        <app-google-auth (googleSignIn)="signInWithGoogle()"></app-google-auth>
        <!-- TODO: Add Apple sign-in when available -->
        <!-- <app-apple-auth (appleSignIn)="signInWithApple()"></app-apple-auth> -->

        <div
          *ngIf="signInLoading"
          class="mt-4 text-sm text-gray-600 dark:text-gray-400"
        >
          {{ authMessages.signInLoadingText }}
        </div>

        <div
          *ngIf="signInError"
          class="mt-2 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm"
        >
          {{ signInError }}
        </div>

        <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
          {{ authMessages.termsText }}
        </p>

        <!-- Close button -->
        <button
          (click)="closeDialog.emit()"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white p-1 rounded-full focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      /* Add slight transition for the dialog appearance */
      :host > div {
        transition: opacity 0.2s ease-in-out;
      }
    `,
  ],
})
export class SignInDialogComponent {
  @Output() closeDialog = new EventEmitter<void>();

  signInLoading = false;
  signInError: string | null = null;
  authMessages = AUTH_MESSAGES;

  constructor(private authService: AuthService) {} // Removed Router

  // Note: No need for toggleSignInDialog here, visibility is controlled by parent

  signInWithGoogle(): void {
    this.signInLoading = true;
    this.signInError = null;
    this.authService
      .signInWithGoogle()
      .then(isNewUser => {
        this.closeDialog.emit(); // Close dialog on success
        // Navigation is handled by AuthService
      })
      .catch(error => {
        console.error('Error during Google sign-in:', error);
        this.signInError = this.authMessages.googleSignInError;
      })
      .finally(() => {
        this.signInLoading = false;
      });
  }

  /*
  TODO: Add Apple sign-in when available
  signInWithApple(): void {
    this.signInLoading = true;
    this.signInError = null;
    this.authService.signInWithApple()
      .then((isNewUser) => {
         this.closeDialog.emit(); // Close dialog on success
         // Navigation is handled by AuthService
      })
      .catch(error => {
        console.error('Error during Apple sign-in:', error);
        this.signInError = typeof error === 'string' ? error : 'An error occurred with Apple sign-in. Please try again.';
      })
      .finally(() => {
        this.signInLoading = false;
      });
  }
  */
}
